/** @format */

import {
  GetRecommendationsServiceOptions,
  IRecommendationsRequestBody,
  IRecommendationsRequestProductDetail,
  IRecommendationsResponse,
  IRecommendationsResult,
} from '../interfaces/recommendations.interface';
import { Injectable, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';
import { Observable, catchError, of, tap, throwError } from 'rxjs';

import { isPlatformServer } from '@angular/common';
import { Logger } from '@shared/data-access/services';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { ProductRecommendationsDao } from '../dao/product.recommendations.dao';
import { map } from 'rxjs/operators';

/** @dynamic */
@Injectable({
  providedIn: 'root',
})
export class ProductRecommendationsService {
  private readonly recommendationsDao = inject(ProductRecommendationsDao);
  private readonly state = inject(TransferState);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly logger = inject(Logger);

  /**
   * @description
   * Accepts several parameters and uses them to construct an object used as the request body for getting recommended products. If
   * the eCode list is not valid or empty, an empty recommendations result object will be returned.
   *
   * @param eCodeList -- The list of eCodes to provide to ds-recs-details to find recommendations for
   * @param placementId -- The placement ID string of the type of recommendations to get back (e.g. others-you-may-like)
   * @param filter -- The filter to apply for getting recommendations (e.g. filterOutOfStockItems)
   * @param priceRerankLevel -- The price re-rank level string (e.g. no-price-reranking)
   * @param diversityLevel -- The diversity level string (e.g. no-diversity)
   * @param eventType -- The event type string (e.g. shopping-cart-page-view)
   * @param athleteId -- The athlete ID string
   * @param maxNumberOfProductsToReturn -- The maximum number of recommended products to return
   * @param project
   * @param purchaseTransaction
   * @param pageType
   * @param containerID
   * @param dsgRecs
   * @param options
   */
  public getRecommendationsWithProductDetails$({
    eCodeList,
    placementId,
    filter,
    priceRerankLevel,
    diversityLevel,
    eventType,
    athleteId,
    maxNumberOfProductsToReturn,
    project,
    purchaseTransaction,
    pageType,
    containerID,
    dsgRecs,
    timeoutMs,
    useTransferState = false,
  }: GetRecommendationsServiceOptions): Observable<IRecommendationsResult> {
    if (useTransferState && athleteId) {
      const error = new Error('TransferState recommendations must not include AthleteID.');
      this.logger.error(error.message);
      return throwError(() => error);
    }
    // Ensure there are eCodes to get recommendations for
    if (!Array.isArray(eCodeList) || (Array.isArray(eCodeList) && eCodeList.length === 0)) {
      // Return an empty recommendations result object (no results, belowMinimumRecommendations = false)
      return of({ results: [], belowMinimumRecommendations: false });
    }

    // Construct the list of product details for the request
    const productDetails: IRecommendationsRequestProductDetail[] = eCodeList.map(
      (eCode: string) =>
        ({
          product: {
            id: eCode,
          },
          quantity: 1,
        }) as IRecommendationsRequestProductDetail,
    );

    // Construct recommendations request body
    let requestBody: IRecommendationsRequestBody = {
      validateOnly: false,
      params: {},
      userEvent: {
        visitorId: athleteId,
        eventType,
        productDetails,
      },
    };

    // Add purchase transaction to request body if event type is "purchase-complete" - BOTF-1739
    if (purchaseTransaction?.id) {
      requestBody.userEvent.purchaseTransaction = purchaseTransaction;
    }
    // Check to see if the filter param has been provided
    if (typeof filter === 'string' && filter.trim() !== '') {
      requestBody = { filter, ...requestBody };
    }

    // Check to see if the diversity level param has been provided
    if (typeof diversityLevel === 'string' && diversityLevel.trim() !== '') {
      requestBody.params = { diversityLevel, ...requestBody.params };
    }

    // Check to see if the price re-rank param has been provided
    if (typeof priceRerankLevel === 'string' && priceRerankLevel.trim() !== '') {
      requestBody.params = { priceRerankLevel, ...requestBody.params };
    }

    const requestTimeoutMs =
      timeoutMs ?? (useTransferState ? DCSG_CONSTANTS.timeouts.xshort : undefined);
    const daoOptions = {
      requestBody,
      placementId,
      maxNumberOfProductsToReturn,
      project,
      returnAllProductDetails: false,
      returnOutOfStockProducts: false,
      pageType,
      containerID,
      dsgRecs,
      ...(requestTimeoutMs === undefined ? {} : { timeoutMs: requestTimeoutMs }),
    };
    const stateKey = makeStateKey<IRecommendationsResult | null>('data-science-recommendations');
    if (useTransferState && this.state.hasKey(stateKey)) {
      const transferred = this.state.get(stateKey, null);
      return transferred
        ? of(transferred)
        : throwError(
            () =>
              new Error(
                '[Product Recommendations Service] Failed to get SSR DS recs, not retrying',
              ),
          );
    }

    const recommendations$ = this.recommendationsDao
      .getRecommendationsWithProductDetails$(daoOptions)
      .pipe(
        map(
          (response: IRecommendationsResponse): IRecommendationsResult => ({
            results: response.results,
            belowMinimumRecommendations:
              response.sourceTrackingInfo?.belowMinimumRecommendations ?? false,
            placementId: response.sourceTrackingInfo?.additionalInfo?.placementId,
          }),
        ),
      );

    if (!useTransferState) {
      return recommendations$;
    }

    return recommendations$.pipe(
      tap((result) => {
        if (isPlatformServer(this.platformId)) {
          this.state.set(stateKey, result);
        }
      }),
      catchError((error: unknown) => {
        this.logger.error(
          '[Product Recommendations Service] Error getting DS recs recommendations with transfer state',
          error,
        );
        if (isPlatformServer(this.platformId)) {
          this.state.set(stateKey, null);
        }
        return throwError(() => error);
      }),
    );
  }
}
